import React, { useState } from 'react';

import { Button, createStyles, Group, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import MovementSourceSelect from 'components/form-fields/auto-complete-fields/MovementSourceSelect';
import { useAuth } from 'context/auth-context';

import ModalMovementForm from './components/ModalMovementForm';
import MovementsTableContainer from './components/MovementsTableContainer';

const useStyles = createStyles(theme => ({
  container: {
    padding: theme.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
  },
}));

const MovementLibrary = (): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const { hasPerm } = useAuth();
  const canMutate = hasPerm('WorkoutMutate');

  const [search, setSearch] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [debouncedSearch] = useDebouncedValue<string>(search, 300);

  const [createFormOpened, { open: openCreateForm, close: closeCreateForm }] = useDisclosure(false);

  return (
    <div className={classes.container}>
      <Stack spacing='md' sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <Group position='apart' align='flex-end'>
          <Group spacing='md'>
            <TextInput
              placeholder='Search movements...'
              icon={<IconSearch size={16} />}
              value={search}
              onChange={e => setSearch(e.currentTarget.value)}
              style={{ minWidth: 300 }}
            />
            <MovementSourceSelect
              value={sourceFilter}
              onChange={setSourceFilter}
              style={{ minWidth: 200 }}
            />
          </Group>

          <Button
            leftIcon={<IconPlus size={theme.fontSizes.md} />}
            onClick={openCreateForm}
            disabled={!canMutate}
            data-cy='createMovementButton'
          >
            New Movement
          </Button>
        </Group>

        <Text color='dimmed' size='sm'>
          Individual exercise that can be performed, tracked, and used within workouts. Examples: Burpees,
          Pull-ups, and Thrusters.
        </Text>

        <MovementsTableContainer
          permission={canMutate}
          search={debouncedSearch}
          source={sourceFilter ?? undefined}
        />

        <ModalMovementForm opened={createFormOpened} closeHandler={closeCreateForm} type='create' />
      </Stack>
    </div>
  );
};

export default MovementLibrary;
